import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Skeleton,
} from "@nexus/ui"
import { useRef } from "react"
import { useTranslation } from "react-i18next"
import type { Opportunity } from "../../../types/opportunities.types"
import { formatOpportunityEngineLabel } from "../../../lib/format-opportunity"
import { OpportunityStatusBadge } from "../../table/OpportunityStatusBadge/OpportunityStatusBadge"
import { OpportunityActionBadge } from "../../table/OpportunityActionBadge/OpportunityActionBadge"
import { OpportunityMetrics } from "../OpportunityMetrics/OpportunityMetrics"
import { OpportunityFindings } from "../OpportunityFindings/OpportunityFindings"
import { OpportunityCreatives } from "../OpportunityCreatives/OpportunityCreatives"

export interface OpportunityDrawerProps {
  opportunity: Opportunity | null
  open: boolean
  onOpenChange: (open: boolean) => void
  isLoading?: boolean
}

// The drawer is non-modal, so interactions outside it are not trapped and would
// otherwise dismiss it. Keep it open when the user interacts with chrome that
// legitimately lives outside the drawer:
// - the opportunities table (row clicks that swap the drawer's opportunity)
// - the top nav (data-top-nav)
// - any floating menu/popover. Nav dropdowns open on hover and Radix auto-focuses
//   their portaled content, which fires the drawer's focus-outside handler. That
//   content is portaled out of the nav, so match the Radix popper wrapper / menu role.
const KEEP_DRAWER_OPEN_SELECTORS = [
  "[data-opportunities-table]",
  "[data-top-nav]",
  "[role='menu']",
  "[data-radix-popper-content-wrapper]",
]

export function shouldKeepDrawerOpenOnOutsideInteraction(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  return KEEP_DRAWER_OPEN_SELECTORS.some((selector) => target.closest(selector) !== null)
}

function DrawerSkeleton() {
  return (
    <>
      {/* Header skeleton */}
      <div className="border-b px-6 pb-5 pt-6 pl-10">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-3.5 w-full" />
          <Skeleton className="h-3.5 w-2/3" />
          <div className="mt-1 flex items-center gap-2">
            <Skeleton className="h-3 w-36" />
            <Skeleton className="h-5 w-14 rounded-full" />
            <Skeleton className="h-5 w-12 rounded-full" />
          </div>
        </div>
      </div>
      {/* Metrics skeleton */}
      <div className="px-6 py-5">
        <Skeleton className="mb-4 h-3 w-28" />
        <div className="grid grid-cols-3 gap-3">
          <Skeleton className="h-[80px] rounded-lg" />
          <Skeleton className="h-[80px] rounded-lg" />
          <Skeleton className="h-[80px] rounded-lg" />
        </div>
      </div>
      {/* Findings skeleton */}
      <div className="px-6 py-5">
        <Skeleton className="mb-4 h-3 w-20" />
        <div className="flex flex-col gap-4">
          <div className="flex gap-3">
            <Skeleton className="h-full w-[3px] rounded-full" />
            <Skeleton className="h-12 flex-1 rounded" />
          </div>
          <div className="flex gap-3">
            <Skeleton className="h-full w-[3px] rounded-full" />
            <Skeleton className="h-12 flex-1 rounded" />
          </div>
        </div>
      </div>
      {/* Creatives skeleton */}
      <div className="px-6 py-5">
        <Skeleton className="mb-4 h-3 w-32" />
        <div className="mb-4 flex gap-3">
          <Skeleton className="h-4 w-14" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-20" />
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="overflow-hidden rounded-md border">
              <Skeleton className="aspect-[4/5] rounded-none" />
              <div className="px-2.5 py-2">
                <Skeleton className="h-3.5 w-2/3" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

export function OpportunityDrawer({ opportunity, open, onOpenChange, isLoading }: OpportunityDrawerProps) {
  const { t } = useTranslation()
  // The drawer body is the scroll container; the creatives grid virtualizes
  // against it to keep off-screen cards (and their decoded media) unmounted.
  const bodyScrollRef = useRef<HTMLDivElement>(null)

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        closePosition="left"
        closeIcon="chevron-right"
        className="flex w-full flex-col overflow-y-auto bg-card p-0 sm:max-w-[1100px] 2xl:max-w-[1300px]"
        onPointerDownOutside={(e) => {
          if (shouldKeepDrawerOpenOnOutsideInteraction(e.target)) {
            e.preventDefault()
          }
        }}
        onInteractOutside={(e) => {
          if (shouldKeepDrawerOpenOnOutsideInteraction(e.target)) {
            e.preventDefault()
          }
        }}
      >
        {isLoading ? (
          <DrawerSkeleton />
        ) : opportunity ? (
          <>
            {/* Header */}
            <SheetHeader className="border-b px-6 pb-5 pt-6">
              {/* pl-10 clears the absolute chevron close button (left-4, size-4) */}
              <div className="flex flex-col gap-1.5 pl-10">
                <SheetTitle className="break-words hyphens-auto text-base leading-snug">
                  {opportunity.topicTitle}
                </SheetTitle>
                <SheetDescription className="text-sm leading-snug text-foreground/70">
                  {opportunity.topicDescription}
                </SheetDescription>
                {/* Metadata strip with badges */}
                <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] text-muted-foreground/60">
                  <span>{opportunity.flowName}</span>
                  <span aria-hidden="true">·</span>
                  <span>{formatOpportunityEngineLabel(opportunity.engine, t)}</span>
                  <span aria-hidden="true">·</span>
                  <span>{opportunity.feature}</span>
                  {opportunity.subFeature !== "Main" && (
                    <>
                      <span aria-hidden="true">/</span>
                      <span>{opportunity.subFeature}</span>
                    </>
                  )}
                  <span aria-hidden="true">·</span>
                  <span className="capitalize">{opportunity.industry.replace(/_/g, " ")}</span>
                  <span aria-hidden="true" className="mx-1 opacity-30">|</span>
                  <OpportunityStatusBadge status={opportunity.status} />
                  <OpportunityActionBadge action={opportunity.action} />
                </div>
              </div>
            </SheetHeader>

            {/* Body — the scroll container the creatives grid virtualizes against.
                `min-h-0` lets this flex child shrink below its content height so its
                own overflow-y scroll engages; without it the child grows to fit all
                content and the sheet shell scrolls instead, which would defeat the
                grid virtualization (and silently disable in-body infinite scroll). */}
            <div ref={bodyScrollRef} className="flex min-h-0 flex-1 flex-col overflow-y-auto">
              {/* Metrics */}
              <div className="px-6 py-5">
                <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t("opportunities.drawer.spendComparison")}
                </p>
                <OpportunityMetrics
                  yourSpend={opportunity.spend}
                  yourSpendPerDataset={opportunity.spendPerDataset}
                  competitorSpend={opportunity.competitorSpend}
                  competitorSpendPerDataset={opportunity.competitorSpendPerDataset}
                  benchmarkSpend={opportunity.benchmarkSpend}
                  benchmarkSpendPerDataset={opportunity.benchmarkSpendPerDataset}
                  flowName={opportunity.flowName}
                />
              </div>

              {/* Findings */}
              {(opportunity.rejectionReason || opportunity.findings || opportunity.suggestion) && (
                <div className="px-6 py-5">
                  <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t("opportunities.drawer.analysis")}
                  </p>
                  {opportunity.rejectionReason && (
                    <div className="mb-3 rounded-md border border-destructive/25 bg-destructive/5 px-3.5 py-3">
                      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-destructive">
                        {t("opportunities.drawer.rejectionReason")}
                      </p>
                      <p className="text-sm leading-relaxed text-foreground">{opportunity.rejectionReason}</p>
                    </div>
                  )}
                  <OpportunityFindings
                    finding={opportunity.findings}
                    suggestion={opportunity.suggestion}
                  />
                </div>
              )}

              {/* Creatives */}
              <div className="px-6 py-5">
                <OpportunityCreatives
                  opportunityId={opportunity.id}
                  opportunitySetId={opportunity.opportunitySetId}
                  opportunityTitle={opportunity.topicTitle}
                  segment1={opportunity.segment1}
                  segment2={opportunity.segment2}
                  segment3={opportunity.segment3}
                  feature={opportunity.feature}
                  subFeature={opportunity.subFeature}
                  flowName={opportunity.flowName}
                  engine={opportunity.engine}
                  industry={opportunity.industry}
                  scrollContainerRef={bodyScrollRef}
                />
              </div>
            </div>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}
